import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { Button } from '@/components/ui/button'
import { QuotesTable } from '@/components/quotes/quotes-table'
import { listQuotesQueryOptions, useQuotesList, useQuoteStats } from '@/stores/quotes.store'
import { organizationStore } from '@/stores/organization.store'
import { QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { DocumentStatCard } from '@/components/shared/financial-document'
import { Skeleton } from 'skullet/react'
import { create } from '@bufbuild/protobuf'
import { GetQuoteStatsResponseSchema, type GetQuoteStatsResponse } from '@/gen/cortado/v1/cortado_pb'
import { QuoteLiteSchema } from '@/gen/cortado/v1/invoices_pb'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { Loader2, Plus, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { canPreloadOrganizationQueries, preloadQueries } from '@/lib/route-preloading'

const quotesSearchSchema = z.object({
  tab: z.enum(['draft', 'open', 'accepted', 'inactive']).optional(),
})

type QuotesTab = 'draft' | 'open' | 'accepted' | 'inactive'

const TAB_STATUSES: Record<QuotesTab, QuoteStatusEnum[]> = {
  draft: [QuoteStatusEnum.QUOTE_STATUS_DRAFT],
  open: [
    QuoteStatusEnum.QUOTE_STATUS_SENT,
    QuoteStatusEnum.QUOTE_STATUS_VIEWED,
    QuoteStatusEnum.QUOTE_STATUS_REVISED,
  ],
  accepted: [QuoteStatusEnum.QUOTE_STATUS_ACCEPTED],
  inactive: [
    QuoteStatusEnum.QUOTE_STATUS_EXPIRED,
    QuoteStatusEnum.QUOTE_STATUS_REJECTED,
    QuoteStatusEnum.QUOTE_STATUS_CANCELLED,
  ],
}

const SAMPLE_STATS: { stats?: GetQuoteStatsResponse } = {
  stats: create(GetQuoteStatsResponseSchema, {
    draftTotalCents: 1245000n,
    draftCount: 4,
    openTotalCents: 3890000n,
    openCount: 12,
    acceptedTotalCents: 9475000n,
    acceptedCount: 27,
    inactiveTotalCents: 612000n,
    inactiveCount: 3,
  }),
}

const SAMPLE_QUOTES = [
  { title: 'Spring wedding', contactName: 'Jordan Ellis', totalCents: 1245000n },
  { title: 'Harbor gala', contactName: 'Priya Ramanathan', totalCents: 3890000n },
  { title: 'Company offsite', contactName: 'Marcus Okafor', totalCents: 845000n },
  { title: 'Retirement dinner', contactName: 'Lena Hart', totalCents: 512000n },
  { title: 'Graduation brunch', contactName: 'Sam Whitaker', totalCents: 298000n },
  { title: 'Fall fundraiser', contactName: 'Ana Duarte', totalCents: 1675000n },
  { title: 'Holiday party', contactName: 'Theo Brandt', totalCents: 2210000n },
  { title: 'Anniversary dinner', contactName: 'Mia Laurent', totalCents: 640000n },
  { title: 'Product launch', contactName: 'Noah Castillo', totalCents: 1980000n },
  { title: 'Rehearsal dinner', contactName: 'Grace Lindqvist', totalCents: 734000n },
].map((quote, i) =>
  create(QuoteLiteSchema, {
    ...quote,
    id: `sample-${i}`,
    quoteNumber: 1040 + i,
    totalWithTipCents: quote.totalCents,
    status: QuoteStatusEnum.QUOTE_STATUS_SENT,
    sentAt: timestampFromDate(new Date(2026, 4, 2 + i)),
    validUntil: timestampFromDate(new Date(2026, 5, 2 + i)),
    createdAt: timestampFromDate(new Date(2026, 4, 1 + i)),
    eventDates: [timestampFromDate(new Date(2026, 6, 12 + i))],
  }),
)

// Degraded stat card shown when the stats RPC errors. The stat cards double as
// the tab buttons, so they must stay rendered and clickable even without data —
// mirrors DocumentStatCard's layout with em-dash placeholder values.
function UnavailableStatCard({
  label,
  isActive,
  onClick,
}: {
  label: string
  isActive?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent/50',
        isActive && 'border-primary bg-primary/5 ring-1 ring-primary',
      )}
    >
      <p className="font-financial text-2xl font-bold">—</p>
      <p className="mt-2 font-medium">{label}</p>
      <p className="mt-1 text-sm text-muted-foreground">—</p>
    </button>
  )
}

export const Route = createFileRoute('/_app/_home/quotes/')({
  component: QuotesPage,
  validateSearch: quotesSearchSchema,
  loaderDeps: ({ search }) => ({
    tab: search.tab,
  }),
  loader: ({ context, deps }) => {
    if (!canPreloadOrganizationQueries(context.auth)) return
    const organizationId = organizationStore.state.selectedOrganizationId
    if (!organizationId) return

    const activeTab: QuotesTab = deps.tab ?? 'open'
    preloadQueries(context.queryClient, [
      listQuotesQueryOptions({
        organizationId,
        statuses: TAB_STATUSES[activeTab],
        limit: 200,
      }),
    ])
  },
})

function QuotesPage() {
  useBreadcrumbFromRoute()

  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const activeTab: QuotesTab = tab ?? 'open'
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim(), 300)

  const { quotes, isLoading, hasLoaded, isPlaceholderData } = useQuotesList({
    statuses: TAB_STATUSES[activeTab],
    limit: 200,
    search: debouncedSearch || undefined,
    keepPreviousData: true,
  })
  const isFilterLoading = search.trim() !== debouncedSearch || isPlaceholderData

  const { stats, hasLoaded: statsHasLoaded } = useQuoteStats()

  const handleTabChange = (value: QuotesTab) => {
    navigate({ search: { tab: value } })
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display-heading text-2xl font-semibold tracking-tight">Quotes</h1>
          <p className="text-sm text-muted-foreground">Manage draft, live, and accepted quotes.</p>
        </div>
        <Button asChild>
          <Link to="/quotes/new">
            <Plus className="h-4 w-4" />
            New Quote
          </Link>
        </Button>
      </div>

      <Skeleton
        data={statsHasLoaded ? { stats } : null}
        sample={SAMPLE_STATS}
        id="quotes:stats"
        label="Loading quote totals"
      >
        {({ stats }) =>
          stats ? (
            <div className="grid auto-rows-min gap-4 md:grid-cols-4">
              <DocumentStatCard
                label="Draft"
                amountCents={Number(stats.draftTotalCents)}
                count={stats.draftCount}
                noun="quote"
                isActive={activeTab === 'draft'}
                onClick={() => handleTabChange('draft')}
              />
              <DocumentStatCard
                label="Live in portal"
                amountCents={Number(stats.openTotalCents)}
                count={stats.openCount}
                noun="quote"
                isActive={activeTab === 'open'}
                onClick={() => handleTabChange('open')}
              />
              <DocumentStatCard
                label="Accepted"
                amountCents={Number(stats.acceptedTotalCents)}
                count={stats.acceptedCount}
                noun="quote"
                isActive={activeTab === 'accepted'}
                onClick={() => handleTabChange('accepted')}
              />
              <DocumentStatCard
                label="Expired / Cancelled"
                amountCents={Number(stats.inactiveTotalCents)}
                count={stats.inactiveCount}
                noun="quote"
                isActive={activeTab === 'inactive'}
                onClick={() => handleTabChange('inactive')}
              />
            </div>
          ) : (
            <div className="grid auto-rows-min gap-4 md:grid-cols-4">
              <UnavailableStatCard
                label="Draft"
                isActive={activeTab === 'draft'}
                onClick={() => handleTabChange('draft')}
              />
              <UnavailableStatCard
                label="Live in portal"
                isActive={activeTab === 'open'}
                onClick={() => handleTabChange('open')}
              />
              <UnavailableStatCard
                label="Accepted"
                isActive={activeTab === 'accepted'}
                onClick={() => handleTabChange('accepted')}
              />
              <UnavailableStatCard
                label="Expired / Cancelled"
                isActive={activeTab === 'inactive'}
                onClick={() => handleTabChange('inactive')}
              />
            </div>
          )
        }
      </Skeleton>

      <InputGroup className="max-w-md">
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
        <InputGroupInput
          type="search"
          aria-label="Search quotes"
          placeholder="Search by number, title, lead name, email, or phone..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {isFilterLoading && (
          <InputGroupAddon align="inline-end">
            <Loader2 className="animate-spin" aria-hidden="true" />
          </InputGroupAddon>
        )}
      </InputGroup>

      <div className="flex-1">
        <Skeleton
          data={isLoading || !hasLoaded ? null : quotes}
          sample={SAMPLE_QUOTES}
          id="quotes:list"
          label="Loading quotes"
        >
          {(quotes) => (
            <QuotesTable
              data={isFilterLoading ? [] : quotes}
              searchQuery={debouncedSearch}
              isFilterLoading={isFilterLoading}
            />
          )}
        </Skeleton>
      </div>
    </div>
  )
}
